import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Calendar, 
  Wallet, 
  Sparkles, 
  ChevronRight,
  ChevronLeft,
  X,
  Sunrise,
  Sun,
  Sunset,
  Moon,
  Clock,
  Star,
  Coffee,
  Utensils,
  Camera,
  Navigation,
  Heart,
  Share2,
  Bookmark,
  Plane,
  Hotel
} from 'lucide-react';

// Destination images mapping
const destinationImages = {
  'goa': 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1200&q=80',
  'manali': 'https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=1200&q=80',
  'jaipur': 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=1200&q=80',
  'kerala': 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=1200&q=80',
  'paris': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1200&q=80',
  'tokyo': 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1200&q=80',
  'bali': 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1200&q=80',
  'dubai': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200&q=80',
  'new york': 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1200&q=80',
  'london': 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1200&q=80',
  'singapore': 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1200&q=80',
  'thailand': 'https://images.unsplash.com/photo-1528181304800-259b08848526?w=1200&q=80',
  'maldives': 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1200&q=80',
  'switzerland': 'https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?w=1200&q=80',
  'default': 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80'
};

const getDestinationImage = (destination) => {
  const key = destination?.toLowerCase() || 'default';
  for (const [place, url] of Object.entries(destinationImages)) {
    if (key.includes(place)) return url;
  }
  return destinationImages.default;
};

const getTimeIcon = (time) => {
  if (!time) return Sun;
  const hour = parseInt(time.split(':')[0]);
  if (hour >= 5 && hour < 12) return Sunrise;
  if (hour >= 12 && hour < 17) return Sun;
  if (hour >= 17 && hour < 20) return Sunset;
  return Moon;
};

const getActivityIcon = (activity) => {
  const name = activity?.toLowerCase() || '';
  if (name.includes('breakfast') || name.includes('coffee')) return Coffee;
  if (name.includes('lunch') || name.includes('dinner') || name.includes('food') || name.includes('restaurant')) return Utensils;
  if (name.includes('photo') || name.includes('view') || name.includes('scenic')) return Camera;
  if (name.includes('temple') || name.includes('church') || name.includes('monument')) return Star;
  return MapPin;
};

const PlanRevealModal = ({ plan, isOpen, onClose, formatCurrency }) => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('overview'); // overview, day-1, day-2, etc.
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance for detection
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentView === 'days') {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrev();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentView('overview');
      setCurrentDayIndex(0);
      setIsAnimating(true);
      // Auto-advance animation
      const timer = setTimeout(() => setIsAnimating(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen || !plan) return null;

  const itinerary = plan.itinerary || [];
  const budget = plan.budget_breakdown || {};
  const destinationImage = getDestinationImage(plan.destination);

  const handleNext = () => {
    if (currentView === 'overview') {
      setCurrentView('days');
      setCurrentDayIndex(0);
    } else if (currentDayIndex < itinerary.length - 1) {
      setCurrentDayIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentView === 'days' && currentDayIndex === 0) {
      setCurrentView('overview');
    } else if (currentDayIndex > 0) {
      setCurrentDayIndex(prev => prev - 1);
    }
  };

  const handleExplore = () => {
    onClose();
    navigate('/result');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 50 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative w-full max-w-4xl max-h-[90vh] bg-white overflow-hidden shadow-2xl"
          style={{ borderRadius: '1.5rem' }}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 bg-white/90 backdrop-blur rounded-full shadow-lg hover:bg-white transition-colors"
            data-testid="close-plan-modal"
          >
            <X size={20} className="text-gray-600" />
          </button>

          {/* Content */}
          <div 
            className="h-[85vh] overflow-y-auto"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <AnimatePresence mode="wait">
              {currentView === 'overview' ? (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="relative"
                >
                  {/* Hero Image */}
                  <div className="relative h-72 md:h-96">
                    <img 
                      src={destinationImage}
                      alt={plan.destination}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    
                    {/* AI Badge */}
                    <motion.div
                      initial={{ y: -20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full"
                    >
                      <Sparkles size={16} className="text-yellow-300" />
                      <span className="text-white text-sm font-medium">AI-Crafted Itinerary</span>
                    </motion.div>

                    {/* Destination Title */}
                    <div className="absolute bottom-6 left-6 right-6">
                      <motion.p
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="text-white/80 text-sm mb-2"
                      >
                        Your personalized adventure to
                      </motion.p>
                      <motion.h1
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="text-4xl md:text-5xl font-black text-white"
                      >
                        {plan.destination}
                      </motion.h1>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="p-6">
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.6 }}
                      className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
                    >
                      <div className="p-4 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl border border-teal-100">
                        <Calendar size={24} className="text-teal-600 mb-2" />
                        <p className="text-2xl font-bold text-gray-900">{plan.duration_days}</p>
                        <p className="text-sm text-gray-500">Days</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border border-emerald-100">
                        <Wallet size={24} className="text-emerald-600 mb-2" />
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(budget.total)}</p>
                        <p className="text-sm text-gray-500">Budget</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl border border-purple-100">
                        <Navigation size={24} className="text-purple-600 mb-2" />
                        <p className="text-2xl font-bold text-gray-900 truncate">{plan.origin || 'N/A'}</p>
                        <p className="text-sm text-gray-500">From</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100">
                        <Star size={24} className="text-amber-600 mb-2" />
                        <p className="text-2xl font-bold text-gray-900">
                          {itinerary.reduce((acc, day) => acc + (day.activities?.length || 0), 0)}
                        </p>
                        <p className="text-sm text-gray-500">Activities</p>
                      </div>
                    </motion.div>

                    {/* Day Preview Cards */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Your Journey</h3>
                      <div className="space-y-3">
                        {itinerary.slice(0, 3).map((day, index) => (
                          <motion.div
                            key={index}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.8 + index * 0.1 }}
                            className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors cursor-pointer"
                            onClick={() => {
                              setCurrentView('days');
                              setCurrentDayIndex(index);
                            }}
                          >
                            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center text-white font-bold">
                              {day.day}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{day.theme || `Day ${day.day}`}</p>
                              <p className="text-sm text-gray-500">{day.activities?.length || 0} activities planned</p>
                            </div>
                            <ChevronRight size={20} className="text-gray-400" />
                          </motion.div>
                        ))}
                        {itinerary.length > 3 && (
                          <p className="text-center text-sm text-gray-500">
                            +{itinerary.length - 3} more days
                          </p>
                        )}
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={`day-${currentDayIndex}`}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="p-6"
                >
                  {/* Day Header */}
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-teal-500/30">
                        {itinerary[currentDayIndex]?.day}
                      </div>
                      <div>
                        <p className="text-sm text-teal-600 font-medium">Day {itinerary[currentDayIndex]?.day} of {plan.duration_days}</p>
                        <h2 className="text-2xl font-bold text-gray-900">
                          {itinerary[currentDayIndex]?.theme || `Exploring ${plan.destination}`}
                        </h2>
                      </div>
                    </div>
                  </div>

                  {/* Activities Timeline */}
                  <div className="space-y-4">
                    {itinerary[currentDayIndex]?.activities?.map((activity, actIndex) => {
                      const TimeIcon = getTimeIcon(activity.time);
                      const ActivityIcon = getActivityIcon(activity.name);
                      
                      return (
                        <motion.div
                          key={actIndex}
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: actIndex * 0.1 }}
                          className="relative flex gap-4"
                        >
                          {/* Timeline Line */}
                          {actIndex < itinerary[currentDayIndex].activities.length - 1 && (
                            <div className="absolute left-6 top-14 bottom-0 w-0.5 bg-gradient-to-b from-teal-300 to-transparent" />
                          )}
                          
                          {/* Time Badge */}
                          <div className="flex-shrink-0 w-12 text-center">
                            <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl flex items-center justify-center mb-1">
                              <TimeIcon size={20} className="text-teal-600" />
                            </div>
                            <p className="text-xs font-medium text-gray-500">{activity.time || '--:--'}</p>
                          </div>

                          {/* Activity Card */}
                          <div className="flex-1 p-4 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <ActivityIcon size={18} className="text-amber-600" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 mb-1">{activity.name}</h4>
                                {activity.description && (
                                  <p className="text-sm text-gray-600 mb-2">{activity.description}</p>
                                )}
                                <div className="flex flex-wrap gap-2">
                                  {activity.duration && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                                      <Clock size={12} />
                                      {activity.duration}
                                    </span>
                                  )}
                                  {activity.cost && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 rounded-full text-xs text-emerald-700">
                                      <Wallet size={12} />
                                      {formatCurrency(activity.cost)}
                                    </span>
                                  )}
                                  {activity.location && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 rounded-full text-xs text-blue-700">
                                      <MapPin size={12} />
                                      {activity.location}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Day Navigation Dots */}
                  <div className="flex justify-center gap-2 mt-8">
                    {itinerary.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentDayIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentDayIndex 
                            ? 'w-8 bg-gradient-to-r from-teal-500 to-cyan-500' 
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Bottom Actions */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white to-transparent">
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={handlePrev}
                disabled={currentView === 'overview'}
                className={`p-3 rounded-xl transition-all ${
                  currentView === 'overview' 
                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ChevronLeft size={24} />
              </button>

              <button
                onClick={handleExplore}
                className="flex-1 py-4 px-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold rounded-xl shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all flex items-center justify-center gap-2"
                data-testid="explore-full-plan-btn"
              >
                <Sparkles size={20} />
                Explore Full Plan
              </button>

              <button
                onClick={handleNext}
                disabled={currentView === 'days' && currentDayIndex >= itinerary.length - 1}
                className={`p-3 rounded-xl transition-all ${
                  currentView === 'days' && currentDayIndex >= itinerary.length - 1
                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PlanRevealModal;
